import type { RefObject } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

// An agent works through ops, not a mouse, so it has no pointer of its own. Each view marks
// where it draws an agent's presence (a doc caret, a sheet cell, a slide element) with
// `agentMark(...)`; this layer, laid over the views, draws a mouse cursor that glides to each
// mark with the agent's name and what it is doing there, so a person can follow it the way they
// follow another person's pointer.

// A space draws one layer over all its windows, so a window inside it draws none of its own.
export const InsideSpace = createContext(false);
// A space drawn as a desktop gives each window traffic lights and its own status, so a window
// inside it keeps quiet about a connection that is fine.
export const InsideDesktop = createContext(false);

export type AgentAction =
  | "typing"
  | "editing"
  | "reading"
  | "creating"
  | "pointing";

export function agentMark({
  name,
  color,
  action,
}: {
  name: string;
  color: string;
  action: AgentAction;
}): Record<string, string> {
  return {
    "data-agent-mark": name,
    "data-agent-color": color,
    "data-agent-action": action,
  };
}

// The same mark for a view that builds its DOM by hand (the doc's caret), as a dataset.
export const agentMarkData = (mark: {
  name: string;
  color: string;
  action: AgentAction;
}) => ({
  agentMark: mark.name,
  agentColor: mark.color,
  agentAction: mark.action,
});

type Rect = { left: number; top: number; right: number; bottom: number };

// Where an agent's cursor is: its spot as a fraction of the window it is in, so it stays on that
// spot while the window moves, scrolls or zooms, and on screen in the layer's own coordinates.
type Cursor = {
  name: string;
  color: string;
  action: AgentAction;
  where: string | null;
  objectKey: string | null;
  fx: number;
  fy: number;
  x: number;
  y: number;
  // Whether a mark put it there this scan; between an agent's steps it rests where it last was.
  live: boolean;
  seenAtMs: number;
};

// One agent mark found in the layer this scan, with the window it sits in.
export type SeenMark = {
  name: string;
  color: string;
  action: AgentAction;
  rect: Rect;
  object: { key: string; title: string | null; rect: Rect } | null;
};

// The colour live-server gives an agent that names none of its own.
export const agentCursorColor = "#6d28d9";

const scanMs = 120;
// Without the room's agents to go by, a cursor whose marks are gone fades after this moment, so a
// run of quick edits reads as one cursor moving rather than blinking.
const lingerMs = 1500;
const barOffsetPx = 18;
const edgeInsetPx = 14;
const homeInsetPx = 24;
const homeStepPx = 30;

const actionLabel: Record<AgentAction, string> = {
  typing: "typing",
  editing: "editing",
  reading: "reading",
  creating: "creating",
  pointing: "here",
};

// "creating" leads only for the short beat a host shows it (dock tile, then the new window's
// bar); the agent's real work, which may already be landing, takes over right after.
const actionRank: Record<AgentAction, number> = {
  creating: 4,
  reading: 3,
  typing: 2,
  editing: 2,
  pointing: 1,
};

const isAgentAction = (value: string | undefined): value is AgentAction =>
  value !== undefined && value in actionLabel;

const clamp = (value: number, low: number, high: number) =>
  Math.min(Math.max(value, low), Math.max(low, high));

const isOutside = (inner: Rect, outer: Rect) =>
  inner.bottom < outer.top ||
  inner.top > outer.bottom ||
  inner.right < outer.left ||
  inner.left > outer.right;

// One cursor per agent, placed from this scan's marks (rects in the layer's coordinates):
//  - at its most telling mark: a creation the host is showing, then a read, then where it writes;
//  - a mark scrolled out of its window's view puts the cursor on the window's edge nearest it,
//    pointing at where the change is;
//  - an agent with no mark this scan stays where it last was in that window, or, when the room's
//    agents are known, at its place in the corner until it does something; without them it fades.
export function placeCursors({
  previous,
  marks,
  windows,
  agents,
  box,
  nowMs,
}: {
  previous: Map<string, Cursor>;
  marks: SeenMark[];
  windows: Map<string, Rect>;
  agents: { name: string }[] | null;
  box: { width: number; height: number };
  nowMs: number;
}): Map<string, Cursor> {
  const next = new Map<string, Cursor>();
  const rankThisScan = new Map<string, number>();
  for (const mark of marks) {
    const rank = actionRank[mark.action];
    if ((rankThisScan.get(mark.name) ?? -1) > rank) {
      continue;
    }
    rankThisScan.set(mark.name, rank);
    const markX = (mark.rect.left + mark.rect.right) / 2;
    const markY = (mark.rect.top + mark.rect.bottom) / 2;
    const frame = mark.object?.rect ?? null;
    const x =
      frame && isOutside(mark.rect, frame)
        ? clamp(markX, frame.left + edgeInsetPx, frame.right - edgeInsetPx)
        : markX;
    const y =
      frame && isOutside(mark.rect, frame)
        ? clamp(markY, frame.top + barOffsetPx, frame.bottom - edgeInsetPx)
        : markY;
    const width = frame ? frame.right - frame.left : 0;
    const height = frame ? frame.bottom - frame.top : 0;
    next.set(mark.name, {
      name: mark.name,
      color: mark.color,
      action: mark.action,
      where: mark.object?.title ?? null,
      objectKey: mark.object?.key ?? null,
      fx: frame && width > 0 ? (x - frame.left) / width : 0,
      fy: frame && height > 0 ? (y - frame.top) / height : 0,
      x,
      y,
      live: true,
      seenAtMs: nowMs,
    });
  }
  const inRoom = agents ? new Set(agents.map((agent) => agent.name)) : null;
  for (const [name, cursor] of previous) {
    if (next.has(name)) {
      continue;
    }
    if (!inRoom) {
      if (nowMs - cursor.seenAtMs < lingerMs) {
        next.set(name, cursor);
      }
      continue;
    }
    if (!inRoom.has(name)) {
      continue;
    }
    const frame = cursor.objectKey ? windows.get(cursor.objectKey) : undefined;
    if (cursor.objectKey && !frame) {
      // Its window closed: back to its place in the corner below.
      continue;
    }
    next.set(name, {
      ...cursor,
      live: false,
      ...(frame
        ? {
            x: frame.left + cursor.fx * (frame.right - frame.left),
            y: frame.top + cursor.fy * (frame.bottom - frame.top),
          }
        : {}),
    });
  }
  // Every agent in the room has a cursor; one that has not done anything rests in the corner.
  agents?.forEach((agent, index) => {
    if (next.has(agent.name)) {
      return;
    }
    next.set(agent.name, {
      name: agent.name,
      color: previous.get(agent.name)?.color ?? agentCursorColor,
      action: "pointing",
      where: null,
      objectKey: null,
      fx: 0,
      fy: 0,
      x: box.width - homeInsetPx * 5,
      y: box.height - homeInsetPx - index * homeStepPx - homeInsetPx * 2,
      live: false,
      seenAtMs: nowMs,
    });
  });
  return next;
}

// A layer of agent cursors drawn inside another one would draw every agent twice.
export const InsideCursorLayer = createContext(false);

export function AgentCursors({
  containerRef,
  agents,
}: {
  containerRef: RefObject<HTMLElement | null>;
  // The room's agents: each keeps a cursor on screen the whole time, not only while it writes.
  agents?: { name: string }[];
}) {
  const insideLayer = useContext(InsideCursorLayer);
  const [cursors, setCursors] = useState<Map<string, Cursor>>(new Map());
  const agentsRef = useRef(agents ?? null);
  agentsRef.current = agents ?? null;

  useEffect(() => {
    if (insideLayer) {
      return;
    }
    const scan = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const box = container.getBoundingClientRect();
      const inBox = (rect: DOMRect): Rect => ({
        left: rect.left - box.left,
        top: rect.top - box.top,
        right: rect.right - box.left,
        bottom: rect.bottom - box.top,
      });
      const keyOf = (element: HTMLElement) =>
        element.dataset.objectId ?? element.dataset.objectTitle ?? "";
      const windows = new Map<string, Rect>();
      if (container.matches("[data-object-id], [data-object-title]")) {
        windows.set(keyOf(container), inBox(box));
      }
      for (const element of container.querySelectorAll<HTMLElement>(
        "[data-object-id], [data-object-title]"
      )) {
        windows.set(keyOf(element), inBox(element.getBoundingClientRect()));
      }
      const marks: SeenMark[] = [];
      for (const element of container.querySelectorAll<HTMLElement>(
        "[data-agent-mark]"
      )) {
        const rect = element.getBoundingClientRect();
        if (
          (rect.width === 0 && rect.height === 0) ||
          rect.bottom < box.top ||
          rect.top > box.bottom ||
          rect.right < box.left ||
          rect.left > box.right
        ) {
          continue;
        }
        const object = element.closest<HTMLElement>(
          "[data-object-id], [data-object-title]"
        );
        const rawAction = element.dataset.agentAction;
        marks.push({
          name: element.dataset.agentMark ?? "",
          color: element.dataset.agentColor ?? agentCursorColor,
          action: isAgentAction(rawAction) ? rawAction : "pointing",
          rect: inBox(rect),
          object: object
            ? {
                key: keyOf(object),
                title: object.dataset.objectTitle ?? null,
                rect: inBox(object.getBoundingClientRect()),
              }
            : null,
        });
      }
      const nowMs = Date.now();
      setCursors((previous) =>
        placeCursors({
          previous,
          marks,
          windows,
          agents: agentsRef.current,
          box: { width: box.width, height: box.height },
          nowMs,
        })
      );
    };
    const timer = setInterval(scan, scanMs);
    return () => clearInterval(timer);
  }, [containerRef, insideLayer]);

  if (insideLayer) {
    return null;
  }
  return (
    <>
      {[...cursors.values()].map((cursor) => (
        <div
          key={cursor.name}
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 z-[1020] transition-[transform,opacity] duration-500 ease-out motion-reduce:transition-none"
          style={{
            transform: `translate(${cursor.x}px, ${cursor.y}px)`,
            opacity: cursor.live ? 1 : 0.7,
          }}
        >
          <CursorMark
            color={cursor.color}
            label={cursor.name}
            detail={
              cursor.live
                ? `${actionLabel[cursor.action]}${cursor.where ? ` in ${cursor.where}` : ""}`
                : cursor.where
                  ? `in ${cursor.where}`
                  : undefined
            }
          />
        </div>
      ))}
    </>
  );
}

// A participant's pointer: a notched arrow outlined in white with a name pill tucked under it,
// the same for people and agents so the two read as one kind of presence.
export function CursorMark({
  color,
  label,
  detail,
}: {
  color: string;
  label: string;
  detail?: string;
}) {
  return (
    <>
      <svg
        width={18}
        height={22}
        viewBox="0 0 18 22"
        aria-hidden
        className="-mt-px -ml-px block drop-shadow"
      >
        <path
          d="M1.5 1.5v16l4.4-4.1 7.1-.4z"
          fill={color}
          stroke="white"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      </svg>
      <span
        className="-mt-0.5 ml-2.5 inline-block whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium text-white shadow-md"
        style={{ background: color }}
      >
        {label}
        {detail && <span className="font-normal"> · {detail}</span>}
      </span>
    </>
  );
}

// Your own pointer, drawn by the browser so it never lags: the same arrow with a "You" pill.
// Anything with a cursor of its own (text, a window's title bar, a resize handle) keeps it.
export function selfCursor(color: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="42" viewBox="0 0 48 42"><path d="M1.5 1.5v16l4.4-4.1 7.1-.4z" fill="${color}" stroke="white" stroke-width="1.5" stroke-linejoin="round"/><rect x="10" y="20" width="34" height="20" rx="6" fill="${color}"/><text x="27" y="34" text-anchor="middle" font-family="system-ui, sans-serif" font-size="12" font-weight="500" fill="white">You</text></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 2 2, default`;
}

// Where an agent that is only reading an object sits: on the object's bar, so a person sees which
// object it is looking at before it writes anything. Each object view puts it among its actions.
export function AgentReadingMarks({
  peers,
}: {
  peers: {
    clientId: number;
    name: string;
    color: string;
    kind: string;
    reading: boolean;
  }[];
}) {
  return (
    <>
      {peers
        .filter((peer) => peer.kind === "agent" && peer.reading)
        .map((peer) => (
          <span
            key={peer.clientId}
            aria-hidden
            className="inline-block h-px w-px"
            {...agentMark({
              name: peer.name,
              color: peer.color,
              action: "reading",
            })}
          />
        ))}
    </>
  );
}
